import { useCallback } from 'react';
import { useMutation } from '@ts-core/lib/apollo';

import { getErrorMessage } from '@ts-core/lib/apollo';
import { useSnackbarNotifications } from '@ts-core/core/hooks/ui';

import {
  startAssignmentMutation,
  StartAssignmentMutationResult,
  StartAssignmentMutationVariables,
} from '../graphql/mutations/startAssignmentMutation';
import {
  sendAssignmentMutation,
  SendAssignmentMutationResult,
  SendAssignmentMutationVariables,
} from '../graphql/mutations/sendAssignmentAnswerMutation';

import { Assignment } from '../types';

export interface AssignmentsHookPayload {
  onStartAssignment: (assignmentId: string, onSuccess?: (assignment: Assignment) => void) => void;
  startAssignmentLoading: boolean;
  startAssignmentError?: string;

  onSendAssignmentAnswer: (assignmentId: string, input: Record<string, any>, onSuccess?: () => void) => void;
  sendAssignmentAnswerLoading: boolean;
  sendAssignmentAnswerError?: string;
}

export interface AssignmentHookInput {
  courseId: string;
}

export default function useAssignmentActions(input: AssignmentHookInput): AssignmentsHookPayload {
  const { courseId } = input;
  const { showErrorNotification } = useSnackbarNotifications();

  /**
   * Start Assignment
   */
  const [startAssignment, { loading: startAssignmentLoading, error: startAssignmentError }] = useMutation<
    StartAssignmentMutationResult,
    StartAssignmentMutationVariables
  >(startAssignmentMutation, {
    onError: showErrorNotification,
  });
  const onStartAssignment = useCallback(
    (assignmentId: string, onSuccess?: (assignment: Assignment) => void) => {
      startAssignment({ variables: { courseId, assignmentId } }).then((result) => {
        if (result.data?.assignment) {
          onSuccess?.(result.data.assignment);
        }
      });
    },
    [startAssignment, courseId]
  );

  /**
   * Sent Assignment answers
   */
  const [sendAssignmentAnswer, { loading: sendAssignmentAnswerLoading, error: sendAssignmentAnswerError }] = useMutation<
    SendAssignmentMutationResult,
    SendAssignmentMutationVariables
  >(sendAssignmentMutation, { onError: showErrorNotification });
  const onSendAssignmentAnswer = useCallback(
    (assignmentId: string, input: Record<string, any>, onSuccess?: () => void) => {
      sendAssignmentAnswer({ variables: { courseId, assignmentId, input } }).then(onSuccess);
    },
    [sendAssignmentAnswer, courseId]
  );

  return {
    onStartAssignment,
    startAssignmentLoading,
    startAssignmentError: getErrorMessage({ error: startAssignmentError }),

    onSendAssignmentAnswer,
    sendAssignmentAnswerLoading,
    sendAssignmentAnswerError: getErrorMessage({ error: sendAssignmentAnswerError }),
  };
}
